import { v4 } from 'uuid';
import { getRecoil, setRecoil } from 'recoil-nexus';

import { isJSON } from 'lib/helpers/utility';

import {
  generalChatActivePromptConversationsState,
  generalChatActivePromptIdState,
  generalChatGenerating,
  generalChatIdState,
  generalChatLoading,
  generalChatMessageIdState,
  generalChatPromptListState
} from 'components/admin/components/GeneralAIChat/lib/state';

import { isFollowUpFromConversations } from './helpers';
import { assembleChunks, clearChunkBuffer } from './chunkAssembly';

const chunkBuffers = {};
let isGenerationStarted = false;

const updateGeneralChat = (data) => {
  const {
    vertical,
    response: r,
    done = false,
    abort = false,
    isRegenerate = false,
    isDescription = false,
    ...rest
  } = data;
  let response = r;

  if (isJSON(r)) {
    response = JSON.parse(r);
  }

  const activePromptConversations = getRecoil(generalChatActivePromptConversationsState);
  const isFollowUp = isFollowUpFromConversations(activePromptConversations);

  if (isDescription) {
    setRecoil(generalChatPromptListState, (prevPrompts = []) => {
      if (prevPrompts?.[0]?.id === rest?.intelligence_chat_id) {
        const nextPrompts = [...prevPrompts];
        nextPrompts[0] = {
          id: rest?.intelligence_chat_id,
          name: data?.intelligenceChat?.name,
          description: data?.intelligenceChat?.description
        };
        return nextPrompts;
      }
      return prevPrompts;
    });
    return;
  }

  try {
    const intelligenceChatId = getRecoil(generalChatIdState);
    if (rest?.intelligence_chat_id && intelligenceChatId) {
      if (rest?.intelligence_chat_id !== intelligenceChatId) {
        return;
      }
    }

    if (!isGenerationStarted) {
      clearChunkBuffer(chunkBuffers, rest?.intelligence_chat_id);
      if (!isFollowUp) {
        setRecoil(generalChatPromptListState, (prevPrompts) => {
          if (prevPrompts?.[0]?.id === rest?.intelligence_chat_id) {
            return prevPrompts;
          }
          const newPrompt = {
            id: rest?.intelligence_chat_id,
            name: 'New Note'
          };

          return [newPrompt, ...prevPrompts];
        });
      }
      setRecoil(generalChatGenerating, true);
      isGenerationStarted = true;
    }

    if (done || abort) {
      setRecoil(generalChatLoading, false);
      isGenerationStarted = false;
    }

    if (rest?.intelligence_chat_id) {
      setRecoil(generalChatIdState, rest?.intelligence_chat_id);
    }

    setPromptStates({
      done,
      abort,
      response,
      jsonResponse: response,
      path: vertical,
      isRegenerate,
      isFollowUp,
      ...rest
    });
  } catch (error) {
    console.error('❌ Error', error);
  }
};

const setPromptStates = (intelligenceAi) => {
  if (intelligenceAi?.response) {
    setRecoil(generalChatActivePromptIdState, intelligenceAi?.intelligence_chat_id);
    if (intelligenceAi?.done || intelligenceAi?.abort) {
      if (intelligenceAi?.intelligenceChatMessage) {
        setRecoil(generalChatMessageIdState, intelligenceAi?.intelligenceChatMessage?.id);
        setRecoil(generalChatActivePromptConversationsState, (prevState) => {
          let newPrompt = [...prevState];
          if (newPrompt[newPrompt?.length - 1]?.agent === 'ai') {
            newPrompt[newPrompt?.length - 1] = {
              ...intelligenceAi?.intelligenceChatMessage,
              emCPTs: intelligenceAi?.emCPTs,
              note: intelligenceAi?.note
            };
          } else {
            newPrompt = [
              ...prevState,
              {
                ...intelligenceAi?.intelligenceChatMessage,
                emCPTsText: intelligenceAi?.emCPTsText,
                note: intelligenceAi?.note
              }
            ];
          }
          return newPrompt;
        });
      }
      setRecoil(generalChatGenerating, false);
    }

    if (!intelligenceAi?.isNote || intelligenceAi?.done) {
      const chatId = intelligenceAi?.intelligence_chat_id;
      if (!chatId) {
        return;
      }

      const orderId = intelligenceAi?.response.orderId;
      const value = intelligenceAi?.response.value;
      const isLast = intelligenceAi?.done;
      const hasValidOrderId = Number.isInteger(orderId);
      const hasChunkValue = typeof value === 'string';
      const hasValidChunk = hasValidOrderId && hasChunkValue;

      // A fresh stream (orderId 0) starts a new buffer.
      if (orderId === 0) {
        clearChunkBuffer(chunkBuffers, chatId);
      }

      if (!hasValidChunk && !isLast) {
        return;
      }

      const assembledMessage = hasValidChunk
        ? assembleChunks(chunkBuffers, chatId, value, orderId)
        : assembleChunks(chunkBuffers, chatId);

      const finalMessage = intelligenceAi?.intelligenceChatMessage?.message;
      const fullMessage =
        (intelligenceAi?.done || intelligenceAi?.abort) && finalMessage
          ? finalMessage
          : assembledMessage || finalMessage || '';

      if (!fullMessage) {
        return;
      }

      const newMessageObj = {
        id: intelligenceAi?.intelligenceChatMessage?.id || v4(),
        message: fullMessage,
        isNote: intelligenceAi?.isNote,
        note: intelligenceAi?.note,
        emCPTsText: intelligenceAi?.emCPTsText,
        agent: 'ai'
      };

      setRecoil(generalChatActivePromptConversationsState, (prevState) => {
        let newConversation = [...prevState];
        if (newConversation[newConversation?.length - 1]?.agent === 'ai') {
          newConversation[newConversation?.length - 1] = newMessageObj;
        } else {
          newConversation = [...prevState, newMessageObj];
        }
        return newConversation;
      });
    }
  }
};

export default updateGeneralChat;
